import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { AddressUtils } from '@/lib/AddressUtils';
import { CompanyUtils } from '@/lib/CompanyUtils';

const CompanySearchType = {
    Exact: 'exact',
    Partial: 'partial'
};

const ViewFilter = {
    Ungrouped: 'ungrouped',
    Grouped: 'grouped',
    Hidden: 'hidden',
    Blocked: 'blocked'
};

const useCompanyStore = defineStore('company', () => {
    const allCompanies = ref([]);
    const nameSearchType = ref(CompanySearchType.Partial);
    const nameSearchTerm = ref('');
    const addressSearchType = ref(CompanySearchType.Partial);
    const addressSearchTerm = ref('');
    const currentView = ref(ViewFilter.Ungrouped);
    const compUtis = new CompanyUtils();

    async function fetchCompanies() {
        const res = await window.cleanupApi.getCompanies();
        allCompanies.value = res.data;
    }

    async function updateCompanyNotes(companyId, notes) {
        await window.cleanupApi.updateCompanyNotes(companyId, notes);
        await fetchCompanies();
    }

    async function updateCompanyNotes2(company, notes) {
        await window.cleanupApi.updateCompanyNotes(company.id, notes);
        const res = await window.cleanupApi.getCompany(company.id);
        compUtis.fillNotes(res.data, company);
        company.hasOverride = res.data.hasOverride;
    }

    function setNameSearch(type, term) {
        nameSearchType.value = type;
        nameSearchTerm.value = term;
    }

    function setAddressSearch(type, term) {
        addressSearchType.value = type;
        addressSearchTerm.value = term;
    }

    const companies = computed(() => {
        let workingList = allCompanies.value;

        switch (currentView.value) {
            case ViewFilter.Ungrouped:
                workingList = workingList.filter(c => !c.grouped && !c.hidden && !c.migrationBlocked);
                break;

            case ViewFilter.Grouped:
                workingList = workingList.filter(c => c.grouped && !c.hidden && !c.migrationBlocked);
                break;

            case ViewFilter.Hidden:
                workingList = workingList.filter(c => c.hidden);
                break;

            case ViewFilter.Blocked:
                workingList = workingList.filter(c => c.migrationBlocked);
                break;
        }

        if (nameSearchTerm.value) {
            if (nameSearchType.value === CompanySearchType.Partial) {
                workingList = workingList.filter(c => c.name.toLowerCase().includes(nameSearchTerm.value.toLowerCase()));
            }
            else {
                workingList = workingList.filter(c => c.name.toLowerCase() === nameSearchTerm.value.toLowerCase());
            }
        }
        
        if (addressSearchTerm.value) {
            if (addressSearchType.value === CompanySearchType.Partial) {
                workingList = workingList.filter(c => 
                    AddressUtils.combineAddress(c).toLowerCase().includes(addressSearchTerm.value.toLowerCase())
                );
            }
            else {
                workingList = workingList.filter(c =>
                    AddressUtils.combineAddress(c).toLowerCase() === addressSearchTerm.value.toLowerCase()
                );
            }
        }

        return workingList;
    });

    return {
        companies,
        nameSearchType,
        nameSearchTerm,
        addressSearchType,
        addressSearchTerm,
        currentView,
        fetchCompanies,
        setNameSearch,
        setAddressSearch,
        updateCompanyNotes,
        updateCompanyNotes2
    };
});

export { useCompanyStore, CompanySearchType, ViewFilter };
